/**
 * Wall-clock times for a business location (IANA timezone).
 * Calendar UI always shows and edits appointment times in the business timezone,
 * not the browser's local timezone.
 */

export interface WallClockParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const WALL_CLOCK_FORMAT: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  hourCycle: "h23",
};

let cachedFormatter: { timeZone: string; formatter: Intl.DateTimeFormat } | undefined;
let cachedOffsets: { key: string; values: Set<number> } | undefined;

function readPart(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): number {
  const value = parts.find((p) => p.type === type)?.value ?? "0";
  return Number(value);
}

export function parseWallClock(iso: string, timeZone: string): WallClockParts {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) {
    throw new Error(`Invalid datetime: ${iso}`);
  }

  if (cachedFormatter?.timeZone !== timeZone) {
    cachedFormatter = {
      timeZone,
      formatter: new Intl.DateTimeFormat("en-GB", { ...WALL_CLOCK_FORMAT, timeZone }),
    };
  }
  const parts = cachedFormatter.formatter.formatToParts(instant);

  return {
    year: readPart(parts, "year"),
    month: readPart(parts, "month"),
    day: readPart(parts, "day"),
    hour: readPart(parts, "hour"),
    minute: readPart(parts, "minute"),
  };
}

export function wallClockMatchesDateIso(wall: WallClockParts, dateIso: string): boolean {
  const [year, month, day] = dateIso.split("-").map(Number);
  return wall.year === year && wall.month === month && wall.day === day;
}

export function wallClockMinutesFromMidnight(iso: string, timeZone: string): number {
  const { hour, minute } = parseWallClock(iso, timeZone);
  return hour * 60 + minute;
}

/** Enumerate valid instants so a DST gap or repeated hour never silently changes input. */
function possibleWallClockInstants(wall: WallClockParts, timeZone: string): string[] {
  const wallMs = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute);
  const date = new Date(wallMs);
  if (
    date.getUTCFullYear() !== wall.year ||
    date.getUTCMonth() + 1 !== wall.month ||
    date.getUTCDate() !== wall.day ||
    wall.hour < 0 ||
    wall.hour > 23 ||
    wall.minute < 0 ||
    wall.minute > 59
  )
    return [];

  // Sample both sides of nearby transitions, including half-hour and date-line changes.
  const key = `${wall.year}-${wall.month}-${wall.day}:${timeZone}`;
  if (cachedOffsets?.key !== key) {
    const values = new Set<number>();
    const midnight = Date.UTC(wall.year, wall.month - 1, wall.day);
    for (let hour = -48; hour <= 48; hour += 6) {
      const sampleMs = midnight + hour * 3_600_000;
      const sampleWall = parseWallClock(new Date(sampleMs).toISOString(), timeZone);
      values.add(
        Date.UTC(
          sampleWall.year,
          sampleWall.month - 1,
          sampleWall.day,
          sampleWall.hour,
          sampleWall.minute,
        ) - sampleMs,
      );
    }
    cachedOffsets = { key, values };
  }
  const offsets = cachedOffsets.values;
  return [...offsets]
    .map((offset) => new Date(wallMs - offset).toISOString())
    .filter((instant) => {
      const actual = parseWallClock(instant, timeZone);
      return (
        actual.year === wall.year &&
        actual.month === wall.month &&
        actual.day === wall.day &&
        actual.hour === wall.hour &&
        actual.minute === wall.minute
      );
    })
    .sort();
}

export function possibleInstantsFromFormFields(
  date: string,
  time: string,
  timeZone: string,
): string[] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return [];
  const [year, month, day] = date.split("-").map(Number) as [number, number, number];
  const [hour, minute] = time.split(":").map(Number) as [number, number];
  return possibleWallClockInstants({ year, month, day, hour, minute }, timeZone);
}

/** Convert only an unambiguous, existing wall time. Repeated hours require an explicit instant. */
export function wallClockToInstantIso(wall: WallClockParts, timeZone: string): string {
  const instants = possibleWallClockInstants(wall, timeZone);
  if (instants.length === 0)
    throw new Error("This local time does not exist. Choose another time.");
  if (instants.length > 1)
    throw new Error("This local time occurs twice. Choose the intended occurrence.");
  const instant = instants[0];
  if (!instant) throw new Error("Choose a valid local time.");
  return instant;
}

export function scheduledAtFromDateAndMinutes(
  dateIso: string,
  minutesFromMidnight: number,
  timeZone: string,
): string {
  const [year, month, day] = dateIso.split("-").map(Number) as [number, number, number];
  const hours = Math.floor(minutesFromMidnight / 60);
  const minutes = minutesFromMidnight % 60;
  return wallClockToInstantIso({ year, month, day, hour: hours, minute: minutes }, timeZone);
}

const TIME_FORMAT: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

export function formatTimeInZone(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", { ...TIME_FORMAT, timeZone });
}

export function formatTimeRangeInZone(
  iso: string,
  durationMinutes: number,
  timeZone: string,
): string {
  const start = new Date(iso);
  const end = new Date(start.getTime() + durationMinutes * 60_000);
  return `${formatTimeInZone(start.toISOString(), timeZone)} – ${formatTimeInZone(end.toISOString(), timeZone)}`;
}

export function wallClockToFormFields(
  iso: string,
  timeZone: string,
): { date: string; time: string } {
  const wall = parseWallClock(iso, timeZone);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${wall.year}-${pad(wall.month)}-${pad(wall.day)}`,
    time: `${pad(wall.hour)}:${pad(wall.minute)}`,
  };
}

export function scheduledAtFromFormFields(date: string, time: string, timeZone: string): string {
  const [year, month, day] = date.split("-").map(Number) as [number, number, number];
  const [hour, minute] = time.split(":").map(Number) as [number, number];
  return wallClockToInstantIso({ year, month, day, hour, minute }, timeZone);
}

export function isSameCalendarDayInZone(iso: string, dateIso: string, timeZone: string): boolean {
  return wallClockMatchesDateIso(parseWallClock(iso, timeZone), dateIso);
}
